import { platform } from '@/services/platform'
import { Ref } from 'vue'

/**
 * Handle the logic of the BottomSheet.vue component.
 */
export class SheetView {
  public isOpen: boolean = false

  private _sheetElement: HTMLElement | null = null
  private _sheetHeight: number = 0
  private _startTime: number = 0
  private _startY: number = 0
  private _currentY: number = 0

  /**
   * Get the CSS variable that will control the element's position.
   */
  get currentPositionCssVariable(): string {
    return `--sheet-current-position: ${this._currentY}px`
  }

  /**
   * Set the `_sheetElement` value.
   *
   * We don't do this in the constructor because the element doesn't
   * exist at the time we instantiate this class.
   */
  setSheetElement(elementRef: Ref<HTMLElement | null>): void {
    this._sheetElement = elementRef.value
  }

  /**
   * Close the sheet.
   */
  close(): void {
    this.isOpen = false
  }

  /**
   * Open the sheet.
   */
  open(): void {
    if (platform.isDesktopWidth()) {
      // The sheet should only open on tablet and mobile
      return
    }
    this.isOpen = true
    this._currentY = 0
  }

  /**
   * Handle the drag start.
   */
  handleTouchStart(event: TouchEvent): void {
    // This will be used to calculate the swipe velocity
    this._startTime = Date.now()
    // This will be used to calculate the swipe distance
    this._startY = event.touches[0].clientY
    // Update _sheetHeight if the element's height has changed
    this._sheetHeight = this._sheetElement?.offsetHeight || this._sheetHeight
  }

  /**
   * Handle the middle of the drag action.
   */
  handleTouchMove(event: TouchEvent): void {
    // If the sheet's contents are being scrolled, don't reposition it.
    if (this._sheetElement && this._sheetElement.scrollTop > 0) {
      // Reset the sheet's position
      this._currentY = 0
      // When the user stops scrolling, this will be the new
      // starting position.
      this._startY = event.touches[0].clientY
      return
    }

    // The current position of the finger
    const touchY = event.touches[0].clientY
    const distanceMoved = touchY - this._startY

    if (distanceMoved <= 0) {
      // Don't let the component move beyond its initial position
      this._currentY = 0
    } else {
      this._currentY = distanceMoved
    }
  }

  /**
   * Handle the drag end.
   */
  handleTouchEnd(): void {
    const endTime = Date.now()

    // The time spent dragging
    const timeSpan = endTime - this._startTime
    // The velocity the drag action
    const velocity = this._currentY / timeSpan

    // Threshold velocity in pixels per millisecond.
    // There's not much science to this number, it's just
    // a value that feels good.
    const velocityThreshold = 0.15
    const halfComponentHeight = this._sheetHeight / 2

    if (velocity > velocityThreshold || this._currentY > halfComponentHeight) {
      // If the user swiped quick enough or if they
      // swiped to below half the height of the element,
      // close it.
      this.close()
    } else {
      // Spring it back to its initial position
      this._currentY = 0
    }
  }
}
